import WebModel from '../models/WebModel'
import Logger from '../helpers/Logger'
import { randomInt, randomBool } from '../helpers/random'
import { getRandomObjectKey } from '../helpers/obj'
import { uniques } from '../helpers/array'

/* eslint-disable no-magic-numbers */

export const testTrafficPercentage = 1

export const noDiscrepancyBidders = ['medianet', 'adx', 'amazon']

/**
 * Encodes which Exchange is being removed from auction process
 * All removes all, Prebid removes prebid, etc
 */
export const Exchange = {
  Control: 0,
  All: 1, // off
  Prebid: 2,
  Eb: 3,
  S2S: 4,
  PrebidAndEb: 5,
  PrebidAndS2S: 6,
  EbAndS2S: 7
} as const

/**
 * Encodes what type of media is being affected
 * The bidder does not participate in
 * the given exchange(s) for that specific media.
 */
export const Media = {
  All: 0, // off
  Display: 1,
  Video: 2,
  Outstream: 3
} as const

/** Class is responsible for randomly selecting:
 * 1) a bidder to remove,
 * 2) an exchange or exchanges to remove the bidder from,
 * 3) a media or media types that is being affected.
 *
 * Provides a encoded value to indicate what change
 * is being applied. A single control is also created
 * that can be used to compare against.
 *
 * Helper methods allow the outside code decide
 * what changes are applicable based on the test configuration
 * selected by the LiftTest (if any).
 *
 * To extend the media or exchange values:
 * > add a new media or exchange value.
 * > implement the condition outside of this class
 * > add the new values to the partnerLift key in GAM
 *
 * To add a new Bidder:
 * > Add the bidder to one of the current discrepancy tabs in D-tool
 * > If a new discrepancy group is added,
 * > add it to the @getAllBidders function.
 */
export class LiftTest {
  readonly bidder: string | null = null
  readonly exchange: string | null = null
  readonly mediaType: string | null = null
  readonly exchangeCode: number | null = null
  readonly mediaTypeCode: number | null = null
  static readonly instance: LiftTest

  /** Returns a singleton instance of the LiftTest */
  static new(model: IWebViewModel | WebModel): LiftTest {
    // @ts-ignore
    LiftTest.instance = LiftTest.instance || new LiftTest(model)
    return LiftTest.instance
  }

  private constructor(
    model: IWebViewModel | WebModel,
    testPercentage = testTrafficPercentage
  ) {
    const runTest = randomBool(testPercentage)
    if (!runTest) {
      return
    }

    this.bidder = selectBidder(model)
    this.exchange = selectExchange()
    this.exchangeCode = Exchange[this.exchange]
    this.mediaType = selectMediaType()
    this.mediaTypeCode = Media[this.mediaType]

    Logger.debug(`
      Lift Test Results:
      Bidder ${this.bidder}
      Exchange ${this.exchange}
      ExchangeCode ${this.exchangeCode}
      MediaType ${this.mediaType}
      MediaTypeCode ${this.mediaTypeCode}
    `)
  }

  isMediaType<K extends keyof typeof Media>(
    mediaTypes: Array<keyof K>
  ): boolean {
    if (this.mediaTypeCode === Media.All) {
      return true
    }

    return (
      this.mediaTypeCode !== null &&
      mediaTypes.indexOf(this.mediaTypeCode) !== -1
    )
  }

  isDisplay(): boolean {
    return this.isMediaType([Media.All, Media.Display])
  }

  isVideo(): boolean {
    return this.isMediaType([Media.All, Media.Video])
  }

  isOutstream(): boolean {
    return this.isMediaType([Media.All, Media.Outstream])
  }

  isExchange<K extends keyof typeof Exchange>(
    exchanges: Array<keyof K>
  ): boolean {
    if (this.exchangeCode === Exchange.All) {
      return true
    }
    return (
      this.exchangeCode !== null && exchanges.indexOf(this.exchangeCode) !== -1
    )
  }

  isPrebid(): boolean {
    return this.isExchange([
      Exchange.Prebid,
      Exchange.PrebidAndEb,
      Exchange.PrebidAndS2S,
      Exchange.All
    ])
  }

  isEb(): boolean {
    return this.isExchange([
      Exchange.Eb,
      Exchange.PrebidAndEb,
      Exchange.EbAndS2S,
      Exchange.All
    ])
  }

  isS2S(): boolean {
    return this.isExchange([
      Exchange.S2S,
      Exchange.EbAndS2S,
      Exchange.PrebidAndS2S,
      Exchange.All
    ])
  }

  getPartnerLiftValue(
    { bidder, exchangeCode, mediaTypeCode } = this
  ): Array<string> | undefined {
    const codes: Array<string> = []

    if (bidder) {
      codes.push(`${bidder}E${exchangeCode}M${mediaTypeCode}`)
    }

    if (this.isEb()) {
      codes.push(`ebOff`)
    }

    return codes
  }
}

const getAllBidders = (model: IWebViewModel | WebModel): Array<string> => {
  const allDiscrepancies = {
    ...model.bannerDiscrepancies,
    ...model.videoDiscrepancies,
    ...model.outstreamDiscrepancies
  }

  const bidderArray = [
    ...model.s2sBidders,
    ...Object.keys(allDiscrepancies),
    ...noDiscrepancyBidders
  ]

  return uniques(bidderArray)
}

export const selectBidder = (model: IWebViewModel | WebModel): string => {
  const bidders = getAllBidders(model)
  const bottomOfRange = 0
  const topOfRange = bidders.length - 1
  const randomSelection = randomInt(bottomOfRange, topOfRange)

  return bidders[randomSelection]
}

export const selectExchange = (): string => getRandomObjectKey(Exchange)
export const selectMediaType = (): string => getRandomObjectKey(Media)
